import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import GenericTable from "@surgia/ui/containers/table/generic-table";
import Badge from "@surgia/ui/components/badge";
import { TableColumn } from "@surgia/ui/containers/table/constants";
import { formatDateWithMoment } from "@surgia/utils";
import TruncatedTooltip from "@surgia/ui/components/truncatedTooltip";
import { HUB_PROTECTED_ROUTES } from "../../routes";
interface ICommonProductsProps {
  data: any;
  loading?: boolean;
  currentPage?: number;
  totalPages?: number;
  onPageChange?: (page: number) => void;
  showSelectedBranchColumn?: boolean;
  // New sorting props
  sortState?: { column: string | null; direction: "asc" | "desc" | null };
  onSort?: (sortState: {
    column: string | null;
    direction: "asc" | "desc" | null;
  }) => void;
  // Optional custom view handler for admin usage
  onViewUser?: (userId: string) => void;
}

const UsersTable: React.FC<ICommonProductsProps> = ({
  data,
  loading,
  currentPage,
  totalPages,
  onPageChange,
  showSelectedBranchColumn = true,
  sortState,
  onSort,
  onViewUser,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleViewUser = (userId: string) => {
    if (onViewUser) {
      // Use custom handler if provided (for admin usage)
      onViewUser(userId);
    } else {
      // Default hub navigation
      navigate(HUB_PROTECTED_ROUTES.USER_DETAILS.replace(":userId", userId));
    }
  };

  // Business-specific column configuration
  const baseColumns: TableColumn[] = [
    {
      key: "id",
      translationKey: "ID",
      sortable: true,
      sortType: "number",
      sortKey: "userId",
      render: (val: any) => (
        <div className="relative group">
          <button
            onClick={() => handleViewUser(val.toString())}
            className="cursor-pointer text-purple-700 hover:text-purple-700/80 transition-colors"
          >
            {val}
          </button>
          <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 px-2 py-1 bg-gray-900 text-white text-sm rounded opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-50 min-w-max">
            {`View ${val}`}
          </div>
        </div>
      ),
    },

    {
      key: "name",
      translationKey: "User name",
      sortable: true,
      sortType: "string",
      sortKey: "fullName",
      render: (value: string) => (
        <div className="text-gray-500 flex items-center gap-2">
          <TruncatedTooltip content={value} useTableRowMain={true} as="p" />
        </div>
      ),
    },
    {
      key: "details",
      translationKey: "User details",
      sortable: false,
      render: (value: { email: string; phone: string }) => (
        <div className="text-gray-500">
          <TruncatedTooltip
            content={value.email}
            useTableRowMain={true}
            tooltipClassName="absolute bottom-full left-0 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-50"
            as="p"
          />
          <TruncatedTooltip
            content={value.phone}
            useTableRowMain={true}
            tooltipClassName="absolute bottom-full left-0 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-50"
            as="p"
          />
        </div>
      ),
    },
    {
      key: "role",
      translationKey: "Roles",
      sortable: true,
      sortType: "string",
      sortKey: "userGroups",
      render: (value: { id: number; name: string }[]) => (
        <div className="text-gray-500 flex items-center gap-2">
          {value.length <= 2 ? (
            // Display all roles if 1 or 2
            value.map((role: { id: number; name: string }) => (
              <Badge
                key={role.id}
                variant={
                  role?.name?.toLocaleLowerCase()?.includes("procurement")
                    ? "procurement"
                    : role?.name?.toLocaleLowerCase()?.includes("admin")
                      ? "secondCategory"
                      : "POLY_CLINIC"
                }
                size="sm"
              >
                {role.name}
              </Badge>
            ))
          ) : (
            // Display first role + "+X" badge for remaining
            <>
              <Badge
                variant={
                  value[0]?.name?.toLocaleLowerCase()?.includes("procurement")
                    ? "procurement"
                    : value[0]?.name?.toLocaleLowerCase()?.includes("admin")
                      ? "secondCategory"
                      : "POLY_CLINIC"
                }
                size="sm"
              >
                {value[0].name}
              </Badge>
              <Badge variant="default" size="sm">
                +{value.length - 1}
              </Badge>
            </>
          )}
        </div>
      ),
    },
    {
      key: "branches",
      translationKey: "Assigned Branch",
      sortable: false,
      sortType: "string",
      render: (value: { id: number; name: string }[]) => (
        <div className="text-gray-500 flex items-center gap-2">
          {value.length <= 2 ? (
            // Display all branches if 1 or 2
            value.map((branch: { id: number; name: string }) => (
              <div key={branch.id} className="relative group">
                <Badge
                  variant="default"
                  size="sm"
                  className="truncate max-w-[100px]"
                >
                  {branch.name}
                </Badge>
                <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 px-2 py-1 bg-gray-900 text-white text-sm rounded opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-50 min-w-max">
                  {branch.name}
                </div>
              </div>
            ))
          ) : (
            // Display first branch + "+X" badge for remaining
            <>
              <div className="relative group">
                <Badge
                  variant="default"
                  size="sm"
                  className="truncate max-w-[100px]"
                >
                  {value[0].name}
                </Badge>
                <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 px-2 py-1 bg-gray-900 text-white text-sm rounded opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-50 min-w-max">
                  {value[0].name}
                </div>
              </div>
              <Badge variant="default" size="sm">
                +{value.length - 1}
              </Badge>
            </>
          )}
        </div>
      ),
    },
    {
      key: "createdAt",
      translationKey: "Date Created",
      sortable: true,
      sortType: "date",
      sortKey: "creationDate",
      render: (value: string) => (
        <div className=" text-gray-500 flex items-center gap-2">
          <p className="table-row-main max-w-[150px]">
            {value ? formatDateWithMoment(value) : "-"}
          </p>
        </div>
      ),
    },
    {
      key: "status",
      translationKey: "Status",
      sortable: true,
      sortType: "string",
      sortKey: "profiles.status",
      render: (value: string) => (
        <Badge
          variant={
            value?.toLocaleLowerCase() === "active" ? "PUBLISHED" : "pending"
          }
          className="!rounded-md"
        >
          {value?.toLocaleLowerCase() === "active"
            ? t("shared.table.active")
            : "Pending Invite"}
        </Badge>
      ),
    },
    {
      key: "lastLogin",
      translationKey: "Last Login",
      sortable: true,
      sortType: "date",
      sortKey: "lastLogin",
      render: (value: string) => (
        <div className=" text-gray-500 flex items-center gap-2">
          <p className="table-row-main max-w-[150px]">
            {value ? formatDateWithMoment(value) : "-"}
          </p>
        </div>
      ),
    },
  ];

  // Filter out branches column if showSelectedBranchColumn is false
  const columns = baseColumns.filter(
    (column) => showSelectedBranchColumn || column.key !== "branches"
  );

  return (
    <GenericTable
      columns={columns}
      data={data}
      loading={loading}
      currentPage={currentPage}
      totalPages={totalPages}
      onPageChange={onPageChange}
      sortState={sortState}
      onSort={onSort}
      enableInternalSorting={false}
    />
  );
};

export default UsersTable;
